/**
 * The trigger form itself: react-hook-form bound to the schema, plus the three
 * places a change to one field writes another.
 *
 * The cascades are attached to their field with `subscribe`, so they run
 * whoever wrote it -- a control, a template, a test -- rather than only when
 * one control's handler fires, and without re-rendering the form.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

import {
  defaultOncePerFor,
  legalOncePerFor,
  OncePer,
  TriggerSourceType,
  TriggerTargetType,
  type PlatformEventType,
  type TriggerV2Record,
} from "@/shared/domain/triggers";

import { emptyConditionValues } from "./conditionValues";
import { defaultTargetKindFor, unavailableKindOf } from "./formScope";
import {
  defaultEventSubscriptionValues,
  defaultScheduleValues,
  defaultTriggerFormValues,
  newTargetValues,
  triggerRecordToFormValues,
} from "./triggerFormDefaults";
import {
  makeTriggerFormSchema,
  type EventSubscriptionValues,
  type ScheduleValues,
  type TriggerFormOutput,
  type TriggerFormValues,
} from "./triggerFormSchema";

/**
 * The grain a change to the checked events lands on: the entity's own default
 * for a fresh pick, the nearest legal one when the current choice stops being
 * allowed, and otherwise what the author already chose.
 */
export function nextOncePer(
  before: readonly PlatformEventType[],
  after: readonly PlatformEventType[],
  current: OncePer,
): OncePer {
  const legal = legalOncePerFor(after);
  if (before.length === 0 || !legal.includes(current)) {
    const preferred = defaultOncePerFor(after);
    return legal.includes(preferred)
      ? preferred
      : legal[0] ?? OncePer.Occurrence;
  }
  return current;
}

/** The parts of the form the cascades touch; the whole form satisfies it. */
type CascadingForm = Pick<
  UseFormReturn<TriggerFormValues, unknown, TriggerFormOutput>,
  "subscribe" | "setValue" | "getValues" | "trigger"
>;

/**
 * Wires the cascades onto `form` and returns the function that stops them.
 *
 * One unfiltered subscription, comparing the written field's name exactly: a
 * subscription filtered by name also fires for a write to a parent path, and
 * replacing `firesOn` whole reports that parent's name, so a filtered one
 * would run every cascade on every swap. A `reset` reports no name at all, so
 * seeding an edit runs nothing. No branch writes the field it listens to,
 * which is what keeps the chain from looping.
 */
export function attachTriggerFormCascades(form: CascadingForm): () => void {
  const { subscribe, setValue, getValues, trigger } = form;
  const source = getValues("firesOn");
  const previous = {
    events:
      source.type === TriggerSourceType.Events
        ? source.events
        : ([] as PlatformEventType[]),
  };
  // The source the person toggled away from. Not form state: it is never
  // validated or submitted. It exists so toggling back restores what they had.
  const setAside: {
    events?: EventSubscriptionValues;
    schedule?: ScheduleValues;
  } = {};
  // True while the whole source is being replaced. Writing an object writes
  // each of its registered fields in turn, so a form with a control on every
  // field reports the swap once per field -- including the entity, whose
  // cascade would clear the changes the swap has just restored.
  let replacingSource = false;
  // The source kind the swap below has already applied. A form with a control
  // on the field reports the write more than once, and a second swap on the
  // same kind would set the new source aside under the old kind's name.
  let appliedType = source.type;

  return subscribe({
    formState: { values: true, isSubmitted: true },
    callback: ({ name, values, isSubmitted }) => {
      if (replacingSource) {
        return;
      }

      if (name === "firesOn.type") {
        // The radio wrote the new tag onto the old source's fields. Set those
        // aside under the old tag, then replace the whole source, so a schedule
        // never carries events and a subscription never carries a cron.
        const { type, ...outgoing } = values.firesOn;
        if (type === appliedType) {
          return;
        }
        appliedType = type;
        replacingSource = true;
        try {
          if (type === TriggerSourceType.Schedule) {
            setAside.events = {
              ...outgoing,
              type: TriggerSourceType.Events,
            } as EventSubscriptionValues;
            setValue("firesOn", setAside.schedule ?? defaultScheduleValues());
            // A schedule's output dataset is optional, so "another dataset"
            // chosen but left unpicked becomes no dataset rather than an error
            // on a field that asks for nothing.
            values.targets.forEach((target, index) => {
              if (
                target.type === TriggerTargetType.InvokeAction &&
                target.spec.outputDestination.mode === "dataset" &&
                target.spec.outputDestination.datasetId === null
              ) {
                setValue(
                  `targets.${index}.spec.outputDestination.mode`,
                  "source",
                );
              }
            });
          } else {
            setAside.schedule = {
              ...outgoing,
              type: TriggerSourceType.Schedule,
            } as ScheduleValues;
            const restored =
              setAside.events ?? defaultEventSubscriptionValues();
            setValue("firesOn", restored);
            previous.events = restored.events;
          }
        } finally {
          replacingSource = false;
        }
      }

      if (
        name === "firesOn.entity" &&
        values.firesOn.type === TriggerSourceType.Events
      ) {
        // A new entity is a new vocabulary: the changes and the condition start
        // over. Clearing the changes chains into the grain rule below. A target
        // is otherwise left alone, since an input row means the same under
        // every entity, and patterns a trigger cannot use are simply not sent.
        setValue("firesOn.events", []);
        setValue("condition", emptyConditionValues());
        // Except when the new entity closes the kind it is on -- an invocation
        // cannot invoke an action yet. It is moved to a kind the entity does
        // offer, so the picker never shows a greyed-out option as the chosen
        // one. `values.firesOn` still carries the outgoing events here, but the
        // kinds turn on the entity alone, so reading them now is safe.
        values.targets.forEach((target, index) => {
          if (unavailableKindOf(values.firesOn, target.type) !== null) {
            setValue(
              `targets.${index}`,
              newTargetValues(defaultTargetKindFor(values.firesOn), {
                events: [],
                firesOnSchedule: false,
              }),
            );
          }
        });
      }

      if (
        name === "firesOn.events" &&
        values.firesOn.type === TriggerSourceType.Events
      ) {
        const { events, oncePer } = values.firesOn;
        setValue(
          "firesOn.oncePer",
          nextOncePer(previous.events, events, oncePer),
        );
        previous.events = events;
      }

      // After the first submit, any change re-validates the whole form. With a
      // resolver, react-hook-form refreshes only the changed field's error, so
      // without this an error that depends on another field stays red after the
      // person has fixed it, and the status line over-counts.
      if (isSubmitted === true && name !== undefined) {
        void trigger();
      }
    },
  });
}

export interface UseTriggerFormOptions {
  mode: "create" | "edit";
  /** The stored trigger an edit starts from. */
  initial?: TriggerV2Record;
}

export function useTriggerForm({
  mode,
  initial,
}: UseTriggerFormOptions): UseFormReturn<
  TriggerFormValues,
  unknown,
  TriggerFormOutput
> {
  const schema = React.useMemo(() => makeTriggerFormSchema({ mode }), [mode]);

  const form = useForm<TriggerFormValues, unknown, TriggerFormOutput>({
    resolver: zodResolver(schema),
    defaultValues:
      initial === undefined
        ? defaultTriggerFormValues()
        : triggerRecordToFormValues(initial),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const { subscribe, setValue, getValues, trigger } = form;

  React.useEffect(
    () =>
      attachTriggerFormCascades({ subscribe, setValue, getValues, trigger }),
    [subscribe, setValue, getValues, trigger],
  );

  return form;
}
